/**
 * Custom hook for voice functionality (STT/TTS)
 */

import { useCallback, useEffect, useRef } from 'react';
import { useWidgetStore } from '../store/widgetStore';
import { getApiClient } from '../services/api-client';
import type { VoiceState } from '@ai360smartsearch/shared';

interface UseVoiceOptions {
  silenceThreshold?: number;
  language?: string;
  onTranscript?: (text: string) => void;
}

export function useVoice(options: UseVoiceOptions = {}) {
  const {
    silenceThreshold = 2000,
    language = 'es-ES',
    onTranscript,
  } = options;

  const voiceState = useWidgetStore(state => state.voiceState);
  const transcript = useWidgetStore(state => state.transcript);
  const setVoiceState = useWidgetStore(state => state.setVoiceState);
  const setTranscript = useWidgetStore(state => state.setTranscript);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  // Start recording
  const startRecording = useCallback(async () => {
    // Verificar soporte del navegador
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('[AI360] Browser does not support getUserMedia');
      setVoiceState('error');
      alert('Tu navegador no soporta la grabación de audio. Por favor, usa un navegador moderno como Chrome, Firefox o Edge.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        // Send to API for transcription
        if (audioBlob.size > 0) {
          setVoiceState('processing');
          
          try {
            const apiClient = getApiClient();
            const result = await apiClient.transcribe(audioBlob);
            
            setTranscript(result.text);
            setVoiceState('idle');
            
            if (onTranscript) {
              onTranscript(result.text);
            }
          } catch (error) {
            console.error('[AI360] Transcription error:', error);
            setVoiceState('idle');
          }
        }
      };

      mediaRecorder.start();
      setVoiceState('listening');

      // Auto-stop after silence threshold
      silenceTimerRef.current = setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
        }
      }, silenceThreshold);

    } catch (error: any) {
      console.error('[AI360] Microphone access error:', error.name, error.message);
      
      // Mostrar mensaje al usuario según el tipo de error
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        alert('Por favor, permite el acceso al micrófono en tu navegador para usar la búsqueda por voz.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        alert('No se encontró ningún micrófono. Por favor, conecta un micrófono y vuelve a intentarlo.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        alert('No se pudo acceder al micrófono. Es posible que esté siendo utilizado por otra aplicación.');
      } else {
        alert('Error al acceder al micrófono: ' + error.message);
      }
      
      setVoiceState('idle');
    }
  }, [silenceThreshold, onTranscript, setVoiceState, setTranscript]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    }
  }, []);

  // Text to speech
  const speak = useCallback(async (text: string) => {
    try {
      setVoiceState('speaking');
      
      const apiClient = getApiClient();
      const result = await apiClient.speak(text, language);
      
      if (result.audioData) {
        const audioUrl = URL.createObjectURL(result.audioData);
        const audio = new Audio(audioUrl);
        
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          setVoiceState('idle');
        };
        
        audio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          setVoiceState('idle');
        };
        
        await audio.play();
      } else {
        setVoiceState('idle');
      }
    } catch (error) {
      console.error('TTS error:', error);
      setVoiceState('idle');
    }
  }, [language, setVoiceState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopRecording]);

  return {
    voiceState,
    transcript,
    startRecording,
    stopRecording,
    speak,
    isListening: voiceState === 'listening',
    isProcessing: voiceState === 'processing',
    isSpeaking: voiceState === 'speaking',
  };
}
